import { Component, inject, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { CentrexDeviceModel } from 'src/app/models/centrex-device.model';
import { CentrexDevicesService } from 'src/app/services/centrex-device.service';
import { AccessRightsService } from 'src/app/services/access-rights.service';
import { HistoriqueComponent } from '../../historique/historique.component';
import { MatChipsModule } from '@angular/material/chips';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-devices-form',
  standalone: true,
  templateUrl: './devices-form.component.html',
  styleUrl: './devices-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    HistoriqueComponent,
    MatChipsModule,
    NgxSpinnerModule,
  ],
})
export class DevicesFormComponent implements OnInit {
  form!: FormGroup;

  isEditMode = false;
  isFullAccess = false;
  availableSites: any[] = [];
  isCentrexNg = false;

  private spinner = inject(NgxSpinnerService);

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DevicesFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      device: CentrexDeviceModel;
      edit: boolean;
      organizationId: string;
      platform: string;
      sites: any[];
    },
    private devicesService: CentrexDevicesService,
    private accessRightsService: AccessRightsService,
  ) {
    this.isEditMode = data.edit;
    this.availableSites = data.sites;
    this.isCentrexNg = data.platform === 'Centrex NG';
  }

  ngOnInit(): void {
    this.spinner.show('spinner');

    this.accessRightsService
      .hasNexusCentrexFullAccess()
      .pipe(
        finalize(() => {
          this.spinner.hide('spinner');
        }),
      )
      .subscribe((isAllowed) => {
        this.isFullAccess = isAllowed;
        this.buildForm();
      });
  }

  private buildForm(): void {
    const canEdit = this.isEditMode && this.isFullAccess;

    this.form = this.fb.group({
      name: [{ value: this.data.device.name, disabled: true }],
      extensions: [
        {
          value: this.data.device.extensionIds?.join(', ') ?? '',
          disabled: true,
        },
      ],
      modelName: [{ value: this.data.device.modelName || '—', disabled: true }],
      address: [{ value: this.data.device.address || '—', disabled: true }],
      state: [{ value: this.data.device.state, disabled: true }],
    });

    // ➜ Le site n'existe QUE pour Enreach
    if (!this.isCentrexNg) {
      this.form.addControl(
        'siteName',
        this.fb.control({
          value: this.data.device.siteName ?? null,
          disabled: !canEdit,
        }),
      );
    }
  }

  compareSite = (a: string, b: string): boolean => {
    return a === b;
  };

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (!this.isEditMode || !this.isFullAccess || this.form.invalid) {
      this.dialogRef.close();
      return;
    }

    if (!this.data.device?.id) {
      console.error('Device ID manquant', this.data.device);
      return;
    }

    this.spinner.show('spinner');

    const { name, extensions, siteName } = this.form.getRawValue();

    this.data.device.name = name;

    // this.data.device.extensionIds = extensions
    //   ? extensions.split(',').map((e: string) => e.trim())
    //   : [];

    if (!this.isCentrexNg) {
      this.data.device.siteName = siteName;
    }

    this.devicesService
      .updateDevice(
        this.data.organizationId,
        this.data.platform,
        this.data.device.id,
        this.data.device,
      )
      .pipe(
        finalize(() => {
          this.spinner.hide('spinner');
        }),
      )
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (err) =>
          console.error('Erreur lors de la mise à jour du périphérique', err),
      });
  }
}
